import httpMethod from 'services/update2025/httpMethod';
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import { SecQuestionContext } from "components/sec-question/SecQuestionContext";
import { InputText } from "primereact/inputtext";
import { createQuestion, updateQuestion, validateQuestion } from "services/SecQuestionService";
import { actionUpdate, actionView } from "utils/action";
import { handleError } from "utils/handleError";

export const SecQuestionEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["sec-question", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const questionContext = useContext(SecQuestionContext);
    const { question, resetQuestion, setQuestion, questions, setQuestions, submitted, setSubmitted, questionDialog, setQuestionDialog } = questionContext;
    const [showSpinner, setShowSpinner] = useState(false);
    const [validForm, setValidForm] = useState(false);
    const [titleEditor, setTitleEditor] = useState(t("question.titleCreateDetail"));

    useEffect(() => {
        // create
        if (!question?.id) {
            setTitleEditor(t("question.titleCreateDetail"));
        } else {
            // update
            if (actionUpdate === question.action) {
                setTitleEditor(t("question.titleUpdateDetail"));
            }
            // view
            else {
                setTitleEditor(t("question.titleDetail"));
            }
        }
    });

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _question = { ...question };
        _question[`${name}`] = val;

        setQuestion(_question);
    };

    const onEnabledChange = (e) => {
        let _question = { ...question };
        _question["enabled"] = e.checked;
        setQuestion(_question);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setQuestionDialog(false);
        setValidForm(false);
    };

    const saveQuestionAction = () => {
        setValidForm(true);
        validateQuestion(question)
            .then(() => {
                if (!question?.id) {
                    const dataRequest = { ...question, id: null };
                    // Creating
                    return createQuestion(headers, dataRequest).then((res) => {
                        toast.current.show({
                            severity: "success",
                            summary: t("success", { ns: "common" }),
                            detail: t("inform.created", { ns: "common", object: t("question") }),
                            life: 3000,
                        });
                        setShowSpinner(true);
                        setValidForm(false);
                        setQuestionDialog(false);
                        setSubmitted(true);
                        resetQuestion();
                    });
                } else {
                    // Updating
                    return updateQuestion(headers, question?.id, {
                        content: question.content,
                        enabled: question.enabled,
                    }).then((res) => {
                        toast.current.show({
                            severity: "success",
                            summary: t("success", { ns: "common" }),
                            detail: t("inform.updated", { ns: "common", object: t("question") }),
                            life: 3000,
                        });
                        setValidForm(false);
                        setQuestionDialog(false);
                        setSubmitted(true);
                        resetQuestion();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < questions?.length; i++) {
            if (questions[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const checkView = () => {
        return actionView === question?.action;
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={questionDialog} style={{ width: "50%" }} header={titleEditor.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* nội dung */}
                                <div className="field col-12">
                                    <label htmlFor="content">
                                        {t("content")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputTextarea id="content" value={question?.content || ""} maxLength={200} onChange={(e) => onInputChange(e, "content")} required autoFocus className={classNames({ "p-invalid": validForm && !question?.content })} rows={3} cols={20} readOnly={checkView()} />
                                    {validForm && !question?.content && <small className="text-danger">{t("content.required")}.</small>}
                                </div>
                            </div>
                            <div className="grid">
                                {/* hiệu lực */}
                                <div className="field col-12 " style={{ display: "flex", justifyContent: "left", margin: "auto" }}>
                                    <label htmlFor="enabled" style={{ padding: "0px 6px 0px 0px" }}>
                                        {t("enabled")}
                                    </label>
                                    <Checkbox id="enabled" checked={question?.enabled} onChange={onEnabledChange} readOnly={checkView()} />
                                </div>
                            </div>

                            {checkView() && (
                                <div className="grid">
                                    {/* người tạo */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="createdBy">{t("createdBy", { ns: "common" })}</label>
                                        <InputText id="createdBy" value={question?.createdBy?.username} readOnly />
                                    </div>
                                    {/* ngày tạo */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="createdDate">{t("createdDate", { ns: "common" })}</label>
                                        <InputText id="createdDate" value={question?.createdDate} readOnly />
                                    </div>
                                    {/* người sửa */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="modifiedBy">{t("modifiedBy", { ns: "common" })}</label>
                                        <InputText id="modifiedBy" value={question?.modifiedBy?.username} readOnly />
                                    </div>
                                    {/* ngày sửa */}
                                    <div className="field col-12 md:col-3">
                                        <label htmlFor="modifiedDate">{t("modifiedDate", { ns: "common" })}</label>
                                        <InputText id="modifiedDate" value={question?.modifiedDate} readOnly />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        {/* save */}
                        {!checkView() && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveQuestionAction}
                            />
                        )}
                        {/* close */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
